import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './LoginNovo.css';
import { getApiBaseUrl } from './utils/apiConfig';
import {
  setPreferGoogleLogin,
  clearPreferGoogleLogin,
  shouldAutoStartGoogleLogin,
  markGoogleAutoTried,
} from './utils/googleLoginPreference';
import {
  clearAuthSession,
  fetchSessionUser,
  hasAuthSession,
  markAuthSession,
} from './utils/authSession';
import { LogIn, Lock, Mail, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { GoogleGIcon } from './features/user-management/components/UserFormConnectedAccounts';
import LoginInputOtp from './components/LoginInputOtp';
import { LoadingStepsCarousel } from './features/board';
import ProtectedBrandLogo from './components/ProtectedBrandLogo';

const API_BASE_URL = getApiBaseUrl();

const GOOGLE_ERROR_MESSAGES = {
  not_configured: 'Login Google não está configurado no servidor.',
  tenant: 'Login Google não está habilitado para este ambiente.',
  not_linked: 'Conta Google sem vínculo. Faça login e vincule em Perfil.',
  callback: 'Não foi possível concluir o login com Google.',
  GOOGLE_ALREADY_LINKED: 'Esta conta Google já está vinculada a outro usuário.',
  token_exchange: 'Falha na autenticação com o Google.',
  userinfo: 'Falha ao obter dados da conta Google.',
  invalid_state: 'Sessão OAuth inválida. Tente novamente.',
};

const EMAIL_OTP_ERRORS = {
  invalid_email: 'Informe um e-mail válido.',
  user_not_found: 'Não encontramos uma conta com este e-mail.',
  user_inactive: 'Usuário inativo. Procure o administrador.',
  tenant: 'Conta indisponível. Contate o administrador.',
  not_configured: 'Envio de e-mail não configurado no servidor.',
  MAIL_SEND_FAILED: 'Não foi possível enviar o código. Tente novamente.',
  invalid_code: 'Código inválido.',
  expired: 'Código expirado. Solicite um novo.',
  max_attempts: 'Muitas tentativas. Solicite um novo código.',
  PASSWORD_EXPIRED: 'Sua senha expirou. Altere-a para continuar.',
};

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());

const LOADING_STEPS = [
  { id: 'config', label: 'Carregar configuração' },
  { id: 'orders', label: 'Carregar ordens de serviço' },
  { id: 'team', label: 'Carregar dados da equipe' },
  { id: 'filters', label: 'Pré-carregar técnicos com ordens' },
  { id: 'board', label: 'Carregar informações do Board' },
  { id: 'complete', label: 'Finalizar carregamento' },
];

const LoginNovo = ({ onLoginSuccess, onInitialLoad }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isShowingLoadingSteps, setIsShowingLoadingSteps] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [pendingUser, setPendingUser] = useState(null);
  const [showExpiredForm, setShowExpiredForm] = useState(false);
  const [expiredEmail, setExpiredEmail] = useState('');
  const [expiredFormData, setExpiredFormData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [expiredError, setExpiredError] = useState('');
  const [expiredSuccess, setExpiredSuccess] = useState(false);
  const [isExpiredSubmitting, setIsExpiredSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [loginView, setLoginView] = useState('login'); // 'login' | 'email_otp'
  const [otpEmail, setOtpEmail] = useState('');
  const [otpMasked, setOtpMasked] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpInvalid, setOtpInvalid] = useState(false);
  const otpVerifyLock = useRef(false);

  useEffect(() => {
    document.body.classList.add('login-novo-active');

    const params = new URLSearchParams(window.location.search);
    const googleError = params.get('google_error');
    if (googleError) {
      markGoogleAutoTried();
      setError(GOOGLE_ERROR_MESSAGES[googleError] || GOOGLE_ERROR_MESSAGES.callback);
      params.delete('google_error');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }

    const googleOk = params.get('google_ok') === '1';
    if (googleOk) {
      setPreferGoogleLogin();
      window.history.replaceState({}, '', window.location.pathname);
      (async () => {
        try {
          setGoogleLoading(true);
          markAuthSession();
          const sessionUser = await fetchSessionUser(API_BASE_URL);
          if (sessionUser) {
            localStorage.setItem('userData', JSON.stringify(sessionUser));
            setPendingUser(sessionUser);
            setIsShowingLoadingSteps(true);
          } else {
            clearAuthSession();
            setError(GOOGLE_ERROR_MESSAGES.callback);
          }
        } catch (_) {
          clearAuthSession();
          setError(GOOGLE_ERROR_MESSAGES.callback);
        } finally {
          setGoogleLoading(false);
        }
      })();
      return () => {
        document.body.classList.remove('login-novo-active');
      };
    }

    const checkExistingToken = async () => {
      if (!hasAuthSession()) {
        if (!googleError && shouldAutoStartGoogleLogin()) {
          markGoogleAutoTried();
          const origin = encodeURIComponent(window.location.origin);
          window.location.href = `${API_BASE_URL}/api/auth/google/start?origin=${origin}`;
        }
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        const result = await response.json();

        if (result.success) {
          markAuthSession();
          localStorage.setItem('userData', JSON.stringify(result.user));
          setPendingUser(result.user);
          setIsShowingLoadingSteps(true);
          return;
        }

        const refreshRes = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        const refreshData = await refreshRes.json();

        if (refreshData.success) {
          markAuthSession();
          const verifyRes = await fetch(`${API_BASE_URL}/api/auth/verify`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          });
          const verifyData = await verifyRes.json();

          if (verifyData.success) {
            localStorage.setItem('userData', JSON.stringify(verifyData.user));
            setPendingUser(verifyData.user);
            setIsShowingLoadingSteps(true);
            return;
          }
        }

        clearAuthSession();
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userData');
        if (!googleError && shouldAutoStartGoogleLogin()) {
          markGoogleAutoTried();
          const origin = encodeURIComponent(window.location.origin);
          window.location.href = `${API_BASE_URL}/api/auth/google/start?origin=${origin}`;
        }
      } catch (error) {
        console.error('Erro ao verificar token (login novo):', error);
        clearAuthSession();
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userData');
      }
    };

    checkExistingToken();

    return () => {
      document.body.classList.remove('login-novo-active');
    };
  }, [onLoginSuccess]);

  useEffect(() => {
    if (!isShowingLoadingSteps || !pendingUser) return;

    const totalSteps = LOADING_STEPS.length;
    const STEP_DURATION = 900; // ms por etapa
    const timeouts = [];

    LOADING_STEPS.forEach((step, index) => {
      const timeoutId = setTimeout(() => {
        setCurrentStepIndex(index);
        const value = Math.min(
          95,
          Math.round(((index + 1) / totalSteps) * 100)
        );
        setProgress(value);
      }, index * STEP_DURATION);
      timeouts.push(timeoutId);
    });

    const animationDuration = totalSteps * STEP_DURATION + 400;
    const animationDone = new Promise((resolve) => {
      const id = setTimeout(() => {
        setProgress(100);
        resolve();
      }, animationDuration);
      timeouts.push(id);
    });

    const loadDone = onInitialLoad
      ? Promise.resolve(onInitialLoad(pendingUser)).catch((err) => {
          console.error('Erro no carregamento inicial (login novo):', err);
          // Continua para o usuário poder entrar mesmo com erro
        })
      : Promise.resolve();

    Promise.all([animationDone, loadDone]).then(() => {
      onLoginSuccess(pendingUser, { skipInitialLoadingModal: true });
    });

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [isShowingLoadingSteps, pendingUser, onLoginSuccess, onInitialLoad]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username.trim() || !formData.password.trim()) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: formData.username.trim(),
          password: formData.password
        })
      });

      const result = await response.json();

      if (result.success) {
        clearPreferGoogleLogin();
        markAuthSession();
        const sessionUser = await fetchSessionUser(API_BASE_URL);
        if (!sessionUser) {
          clearAuthSession();
          setError('Sessão inválida após login. Tente novamente.');
          return;
        }
        localStorage.setItem('userData', JSON.stringify(sessionUser));
        setPendingUser(sessionUser);
        setIsShowingLoadingSteps(true);
      } else if (result.code === 'PASSWORD_EXPIRED') {
        setExpiredEmail(formData.username.trim());
        setShowExpiredForm(true);
        setExpiredFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setExpiredError('');
        setFormData((prev) => ({ ...prev, password: '' }));
        setError('');
      } else {
        setError(result.message || 'Erro no login');
      }
    } catch (err) {
      console.error('Erro no login (login novo):', err);
      setError('Erro de conexão. Verifique se o servidor está rodando.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const handleGoogleLogin = () => {
    if (isLoading || googleLoading || otpSending || otpVerifying) return;
    markGoogleAutoTried();
    setPreferGoogleLogin();
    const origin = encodeURIComponent(window.location.origin);
    window.location.href = `${API_BASE_URL}/api/auth/google/start?origin=${origin}`;
  };

  useEffect(() => {
    if (!otpExpiresAt || loginView !== 'email_otp') return undefined;
    const tick = () => {
      const left = Math.max(0, Math.ceil((new Date(otpExpiresAt).getTime() - Date.now()) / 1000));
      setOtpSecondsLeft(left);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [otpExpiresAt, loginView]);

  const startEmailOtp = useCallback(async (emailOverride) => {
    const email = String(emailOverride || formData.username || '').trim().toLowerCase();
    if (!isValidEmail(email)) {
      setError(EMAIL_OTP_ERRORS.invalid_email);
      return;
    }
    if (otpSending) return;
    setOtpSending(true);
    setError('');
    setOtpInvalid(false);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/email-code/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        if (data.code === 'PASSWORD_EXPIRED') {
          setExpiredEmail(email);
          setShowExpiredForm(true);
          setLoginView('login');
          setError('');
          return;
        }
        throw new Error(
          EMAIL_OTP_ERRORS[data.code] || data.message || 'Não foi possível enviar o código.'
        );
      }
      setOtpEmail(email);
      setOtpMasked(data.email_masked || email);
      setOtpCode('');
      setOtpExpiresAt(data.expires_at || new Date(Date.now() + (data.expires_in || 300) * 1000).toISOString());
      setOtpSecondsLeft(Number(data.expires_in) || 300);
      otpVerifyLock.current = false;
      setLoginView('email_otp');
    } catch (err) {
      setError(err.message || EMAIL_OTP_ERRORS.MAIL_SEND_FAILED);
    } finally {
      setOtpSending(false);
    }
  }, [formData.username, otpSending]);

  const verifyEmailOtp = useCallback(async (codeValue) => {
    const code = String(codeValue || otpCode || '').replace(/\D/g, '');
    if (code.length !== 6 || otpVerifyLock.current || otpVerifying) return;
    otpVerifyLock.current = true;
    setOtpVerifying(true);
    setError('');
    setOtpInvalid(false);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/email-code/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: otpEmail, code }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        if (data.code === 'PASSWORD_EXPIRED') {
          setExpiredEmail(otpEmail);
          setShowExpiredForm(true);
          setLoginView('login');
          return;
        }
        setOtpInvalid(true);
        setOtpCode('');
        otpVerifyLock.current = false;
        throw new Error(
          EMAIL_OTP_ERRORS[data.code] || data.message || EMAIL_OTP_ERRORS.invalid_code
        );
      }
      markAuthSession();
      const sessionUser = await fetchSessionUser(API_BASE_URL);
      if (!sessionUser) {
        clearAuthSession();
        throw new Error('Sessão inválida após login. Tente novamente.');
      }
      localStorage.setItem('userData', JSON.stringify(sessionUser));
      clearPreferGoogleLogin();
      setPendingUser(sessionUser);
      setIsShowingLoadingSteps(true);
      setLoginView('login');
    } catch (err) {
      setError(err.message || EMAIL_OTP_ERRORS.invalid_code);
      otpVerifyLock.current = false;
    } finally {
      setOtpVerifying(false);
    }
  }, [otpCode, otpEmail, otpVerifying]);

  const handleOtpComplete = useCallback(
    (code) => {
      verifyEmailOtp(code);
    },
    [verifyEmailOtp]
  );

  const formatCountdown = (sec) => {
    const s = Math.max(0, Number(sec) || 0);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  const handleExpiredInputChange = (field, value) => {
    setExpiredFormData((prev) => ({ ...prev, [field]: value }));
    if (expiredError) setExpiredError('');
  };

  const handleExpiredSubmit = async (e) => {
    e.preventDefault();
    if (!expiredFormData.currentPassword || !expiredFormData.newPassword || !expiredFormData.confirmPassword) {
      setExpiredError('Preencha todos os campos.');
      return;
    }
    if (expiredFormData.newPassword !== expiredFormData.confirmPassword) {
      setExpiredError('A confirmação da nova senha não coincide.');
      return;
    }
    if (expiredFormData.newPassword === expiredFormData.currentPassword) {
      setExpiredError('A nova senha não pode ser igual à senha atual.');
      return;
    }
    setIsExpiredSubmitting(true);
    setExpiredError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/change-expired-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: expiredEmail,
          currentPassword: expiredFormData.currentPassword,
          newPassword: expiredFormData.newPassword
        })
      });
      const result = await res.json();
      if (result.success) {
        setShowExpiredForm(false);
        setExpiredSuccess(true);
        setFormData({ username: expiredEmail, password: '' });
        setExpiredFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setExpiredError(result.message || 'Erro ao atualizar senha.');
      }
    } catch (err) {
      setExpiredError('Erro de conexão. Tente novamente.');
    } finally {
      setIsExpiredSubmitting(false);
    }
  };

  const isLoginFormVisible = !isShowingLoadingSteps;
  const canSubmitPassword =
    Boolean(formData.username.trim()) && Boolean(formData.password.trim()) && !isLoading;

  return (
    <div className="login-novo-page">
      <div className="login-novo-glow" aria-hidden />
      <div className="login-novo-shell">
        <div className="login-novo-brand">
          <ProtectedBrandLogo />
        </div>

        <div className={`login-novo-card${isShowingLoadingSteps ? ' login-novo-card--loading' : ''}`}>
          {showExpiredForm ? (
            <>
              <div className="login-novo-header">
                <h1>Senha expirada</h1>
                <p>Sua senha expirou. Altere-a para continuar.</p>
              </div>
              <form className="login-novo-form" onSubmit={handleExpiredSubmit}>
                <div className="login-novo-field">
                  <label>Usuário</label>
                  <div className="login-novo-input-wrapper login-novo-input-readonly">
                    <input type="text" value={expiredEmail} readOnly className="login-novo-input" />
                  </div>
                </div>
                <div className="login-novo-field">
                  <label htmlFor="expired-current">Senha atual</label>
                  <div className="login-novo-input-wrapper">
                    <Lock size={15} className="login-novo-input-icon" />
                    <input
                      type="password"
                      id="expired-current"
                      value={expiredFormData.currentPassword}
                      onChange={(e) => handleExpiredInputChange('currentPassword', e.target.value)}
                      placeholder="Digite a senha atual"
                      className="login-novo-input"
                      autoComplete="current-password"
                      disabled={isExpiredSubmitting}
                    />
                  </div>
                </div>
                <div className="login-novo-field">
                  <label htmlFor="expired-new">Nova senha</label>
                  <div className="login-novo-input-wrapper">
                    <Lock size={15} className="login-novo-input-icon" />
                    <input
                      type="password"
                      id="expired-new"
                      value={expiredFormData.newPassword}
                      onChange={(e) => handleExpiredInputChange('newPassword', e.target.value)}
                      placeholder="Digite a nova senha"
                      className="login-novo-input"
                      autoComplete="new-password"
                      disabled={isExpiredSubmitting}
                    />
                  </div>
                </div>
                <div className="login-novo-field">
                  <label htmlFor="expired-confirm">Confirmar nova senha</label>
                  <div className="login-novo-input-wrapper">
                    <Lock size={15} className="login-novo-input-icon" />
                    <input
                      type="password"
                      id="expired-confirm"
                      value={expiredFormData.confirmPassword}
                      onChange={(e) => handleExpiredInputChange('confirmPassword', e.target.value)}
                      placeholder="Confirme a nova senha"
                      className="login-novo-input"
                      autoComplete="new-password"
                      disabled={isExpiredSubmitting}
                    />
                  </div>
                </div>
                {expiredError && (
                  <div className="login-novo-error">
                    <i className="bi bi-exclamation-circle" />
                    <span>{expiredError}</span>
                  </div>
                )}
                <button type="submit" className="login-novo-submit" disabled={isExpiredSubmitting}>
                  {isExpiredSubmitting ? (
                    <>
                      <i className="bi bi-arrow-repeat login-novo-spinner" />
                      Atualizando...
                    </>
                  ) : (
                    <>
                      <LogIn size={15} />
                      Atualizar senha
                    </>
                  )}
                </button>
              </form>
            </>
          ) : isLoginFormVisible && loginView === 'email_otp' ? (
            <>
              <div className="login-novo-header">
                <h1>Verifique seu e-mail</h1>
                <p>
                  Enviamos um código de 6 dígitos para{' '}
                  <strong className="login-novo-otp-email">{otpMasked}</strong>
                </p>
              </div>
              <div className="login-novo-form login-novo-otp-form">
                <LoginInputOtp
                  value={otpCode}
                  onChange={(v) => {
                    setOtpCode(v);
                    if (otpInvalid) setOtpInvalid(false);
                    if (error) setError('');
                  }}
                  onComplete={handleOtpComplete}
                  disabled={otpVerifying || otpSending}
                  invalid={otpInvalid}
                />
                <div className="login-novo-otp-meta">
                  {otpSecondsLeft > 0 ? (
                    <span>Código expira em {formatCountdown(otpSecondsLeft)}</span>
                  ) : (
                    <span className="login-novo-otp-expired">Código expirado</span>
                  )}
                </div>
                {error && (
                  <div className="login-novo-error">
                    <i className="bi bi-exclamation-circle" />
                    <span>{error}</span>
                  </div>
                )}
                <button
                  type="button"
                  className="login-novo-submit"
                  disabled={otpVerifying || otpSending || otpCode.length !== 6}
                  onClick={() => verifyEmailOtp(otpCode)}
                >
                  {otpVerifying ? (
                    <>
                      <i className="bi bi-arrow-repeat login-novo-spinner" />
                      Verificando...
                    </>
                  ) : (
                    'Continuar'
                  )}
                </button>
                <div className="login-novo-otp-actions">
                  <button
                    type="button"
                    className="login-novo-forgot-link"
                    disabled={otpSending || otpVerifying}
                    onClick={() => startEmailOtp(otpEmail)}
                  >
                    {otpSending ? 'Reenviando...' : 'Reenviar código'}
                  </button>
                  <button
                    type="button"
                    className="login-novo-forgot-link login-novo-otp-back"
                    disabled={otpVerifying || otpSending}
                    onClick={() => {
                      setLoginView('login');
                      setOtpCode('');
                      setError('');
                      setOtpInvalid(false);
                      otpVerifyLock.current = false;
                    }}
                  >
                    <ArrowLeft size={14} />
                    Voltar
                  </button>
                </div>
              </div>
            </>
          ) : isLoginFormVisible ? (
            <>
              <div className="login-novo-header">
                <h1>Bem-vindo de volta!</h1>
              </div>
              {expiredSuccess && (
                <div className="login-novo-success-msg">
                  <i className="bi bi-check-circle" />
                  Senha alterada. Faça login com a nova senha.
                </div>
              )}
              <div className="login-novo-social">
                <button
                  type="button"
                  className="login-novo-social-btn"
                  onClick={handleGoogleLogin}
                  disabled={isLoading || googleLoading || otpSending}
                >
                  <span className="login-novo-social-icon" aria-hidden>
                    <GoogleGIcon size={18} />
                  </span>
                  {googleLoading ? 'Conectando...' : 'Continuar com o Google'}
                </button>
                <button
                  type="button"
                  className="login-novo-social-btn"
                  onClick={() => startEmailOtp()}
                  disabled={isLoading || googleLoading || otpSending}
                >
                  <span className="login-novo-social-icon" aria-hidden>
                    <Mail size={18} strokeWidth={1.75} />
                  </span>
                  {otpSending ? 'Enviando código...' : 'Continuar com o e-mail'}
                </button>
              </div>
              <div className="login-novo-oauth-divider" aria-hidden>
                <span>ou</span>
              </div>
              <form className="login-novo-form" onSubmit={handleSubmit}>
                <div className="login-novo-field">
                  <div className="login-novo-input-wrapper">
                    <input
                      type="email"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="E-mail de trabalho"
                      className={`login-novo-input${error && !formData.username.trim() ? ' login-novo-input--error' : ''}`}
                      autoComplete="username"
                      autoFocus
                      aria-label="E-mail de trabalho"
                    />
                  </div>
                </div>

                <div className="login-novo-field">
                  <div className="login-novo-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Senha"
                      className="login-novo-input login-novo-input--password"
                      autoComplete="current-password"
                      aria-label="Senha"
                    />
                    <button
                      type="button"
                      className="login-novo-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex="-1"
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="login-novo-error">
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  className="login-novo-submit"
                  disabled={!canSubmitPassword || googleLoading || otpSending}
                >
                  {isLoading ? (
                    <>
                      <i className="bi bi-arrow-repeat login-novo-spinner" />
                      Entrando...
                    </>
                  ) : (
                    'Entre'
                  )}
                </button>
                <button
                  type="button"
                  className="login-novo-forgot-link"
                  onClick={() => navigate('/esqueceusenha')}
                >
                  Esqueceu a senha?
                </button>
              </form>
            </>
          ) : (
            <div className="login-novo-loading-steps-wrapper">
              <div className="login-novo-header">
                <h1>Inicializando sistema</h1>
                <p>Carregando dados e configurações...</p>
              </div>

              <div className="login-novo-progress">
                <div className="login-novo-progress-track">
                  <div
                    className="login-novo-progress-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="login-novo-progress-text">
                  {Math.min(100, Math.round(progress))}% concluído
                </div>
              </div>

              <LoadingStepsCarousel
                className="login-novo-loading-steps-carousel"
                variant="light"
                steps={LOADING_STEPS}
                activeIndex={currentStepIndex}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginNovo;

